import { getModelConfig, resolveModelKey } from '../config/model-registry.js';

export const PLATFORM_PROMPT_LIMIT = 32000;
export const DEFAULT_MODEL_PROMPT_LIMIT = 20000;

// Manufacturer limits only. Sources and qualifications: docs/model-prompt-limits.md.
// Recommendations and token budgets are deliberately NOT converted to characters.
export const PROMPT_POLICIES = {
  sora: { manufacturer: 'OpenAI' },
  seedance: { manufacturer: 'ByteDance' },
  minimax: { manufacturer: 'MiniMax', characterLimit: 7000 },
  veo: { manufacturer: 'Google', tokenLimit: 1024 },
  wan: { manufacturer: 'Alibaba', characterLimit: 20000 },
  omni: { manufacturer: 'Google' },
  agnes: { manufacturer: 'Agnes AI' },
  happyhorse: { manufacturer: 'Alibaba', characterLimit: 5000, chineseCharacterLimit: 2500 },
  kling: { manufacturer: 'Kuaishou', characterLimit: 3072 },
  grok: { manufacturer: 'xAI' },
  nano: { manufacturer: 'Google' },
  seedream: { manufacturer: 'ByteDance' },
  gptImage: { manufacturer: 'OpenAI', characterLimit: 32000 },
};

export function getPromptLengthPolicy(model) {
  const modelKey = resolveModelKey(model) || String(model || '');
  const family = [
    ['sora-', 'sora'], ['seedance-', 'seedance'], ['minimax-', 'minimax'],
    ['veo-', 'veo'], ['wan-', 'wan'], ['gemini-omni', 'omni'],
    ['botgo-', 'agnes'], ['happyhorse-', 'happyhorse'], ['kling-', 'kling'],
    ['grok-', 'grok'], ['nano-banana', 'nano'], ['seedream-', 'seedream'],
    ['gpt-image-', 'gptImage'],
  ].find(([prefix]) => modelKey.startsWith(prefix))?.[1];
  const policy = PROMPT_POLICIES[family] || {};
  return {
    ...policy,
    family: family || 'default',
    modelKey,
    model: getModelConfig(modelKey)?.displayName || modelKey || 'This model',
    limit: Math.min(PLATFORM_PROMPT_LIMIT, policy.characterLimit || DEFAULT_MODEL_PROMPT_LIMIT),
  };
}

export function countPromptCharacters(prompt) {
  return Array.from(typeof prompt === 'string' ? prompt : '').length;
}

export function getPromptLengthError({ modelKey, prompt } = {}) {
  const policy = getPromptLengthPolicy(modelKey);
  const text = typeof prompt === 'string' ? prompt : '';
  const actual = countPromptCharacters(text);
  // HappyHorse does not specify a mixed-language weighting algorithm. Enforce
  // the two documented ceilings independently rather than inventing one.
  const chineseCount = policy.chineseCharacterLimit
    ? Array.from(text).filter((char) => /\p{Script=Han}/u.test(char)).length : 0;
  const chineseExceeded = chineseCount > (policy.chineseCharacterLimit || Infinity);
  if (actual <= policy.limit && !chineseExceeded) return null;
  const errorKey = chineseExceeded ? 'modelPromptChineseLimit' : 'modelPromptLimit';
  const values = {
    model: policy.model,
    limit: chineseExceeded ? policy.chineseCharacterLimit : policy.limit,
    actual: chineseExceeded ? chineseCount : actual,
  };
  return {
    code: 'PROMPT_TOO_LONG', errorKey, values,
    message: `${values.model} supports up to ${values.limit.toLocaleString('en-US')} ${chineseExceeded ? 'Chinese ' : ''}characters in a prompt. Your prompt contains ${values.actual.toLocaleString('en-US')}. Please shorten it and try again.`,
  };
}

export function assertPromptLength(options) {
  const failure = getPromptLengthError(options);
  if (failure) throw Object.assign(new Error(failure.message), failure);
}

// Only inspect prompt-bearing fields, never URLs, base64 media or system metadata.
export function extractPayloadPrompts(payload) {
  if (!payload) return [];
  if (typeof FormData !== 'undefined' && payload instanceof FormData) {
    return payload.getAll('prompt').filter((value) => typeof value === 'string');
  }
  const result = [];
  const add = (value) => { if (typeof value === 'string') result.push(value); };
  const parts = (items) => (Array.isArray(items) ? items : []).map((item) =>
    typeof item?.text === 'string' ? item.text : '').join('');
  add(payload.prompt);
  add(payload.input?.prompt);
  if (typeof payload.input === 'string') add(payload.input);
  if (Array.isArray(payload.input)) add(parts(payload.input));
  if (Array.isArray(payload.content)) add(parts(payload.content));
  for (const instance of payload.instances || []) add(instance.prompt);
  if (Array.isArray(payload.contents)) add(payload.contents.map((item) => typeof item.text === 'string' ? item.text : parts(item.parts)).join(''));
  if (Array.isArray(payload.messages)) add(payload.messages.filter((item) => item.role === 'user')
    .map((item) => typeof item.content === 'string' ? item.content : parts(item.content)).join(''));
  for (const shot of payload.multi_prompt || payload.input?.multi_prompt || []) add(shot.prompt);
  return result;
}

export function assertPayloadPromptLength({ payload, provider, requestData, modelKey } = {}) {
  const candidates = [modelKey, requestData?.modelKey, requestData?.modelName,
    provider?.modelKey, provider?.modelName, provider?.model, payload?.model];
  const resolvedModel = candidates.map((value) => resolveModelKey(value)).find(Boolean)
    || candidates.find(Boolean);
  for (const prompt of extractPayloadPrompts(payload)) {
    assertPromptLength({ modelKey: resolvedModel, prompt });
  }
  return payload;
}
